import { useToastStore } from '@/stores/toast-store';

export function useToast() {
    const toastStore = useToastStore();

    /**
     * Displays a toast notification with the specified message and options.
     *
     * @param {string|Object} toast - The toast content or configuration. If a string is provided, it will be used as the message.
     *                               If an object is provided, it can include the following properties:
     *                               - `heading` (string): The title or heading of the toast.
     *                               - `message` (string): The main message/content of the toast.
     *                               - `variant` (string): The variation of the toast (e.g., 'success', 'error', etc.). Default is 'success'.
     *                               - `duration` (number): The duration (in milliseconds) the toast should be visible. If set to 0, the toast will not auto-dismiss. Default is 5000.
     *                               - `buttons` (Array): An array of button objects for the toast. Each button object can have `text`, `variant`, and `callback`.
     *
     * @return {void} Displays and manages the lifecycle of a toast notification.
     */
    function displayToast(toast) {
        // make it so that even if 2 toasts are called at the same time, they will have different ids
        const id = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

        let variant = 'success';
        let duration = 6000;
        let heading = null;
        let message = toast;
        let buttons = [];
        let links = false;

        if (typeof toast !== 'string') {
            heading = toast.heading ?? null;
            message = toast.message ?? '';
            variant = toast.variant ?? 'success';
            duration = toast.duration !== undefined && toast.duration !== null && toast.duration >= 0 ? toast.duration : 6000;
            buttons = toast.buttons ?? [];
            links = toast.links ?? false;
        }

        toastStore.addToast({ id, message, heading, variant, buttons, links });

        if (duration === 0) return;

        setTimeout(() => {
            toastStore.removeToast(id);
        }, duration);
    }

    return { displayToast };
}
